import { useMutation, useQuery } from '@apollo/client';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Accordion, Col } from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';

import { SearchUserQuery, SpecialEventScoreDetailType, UserSearchType } from '../../../../../../__gqltypes__';
import {
  GET_USER_SPECIAL_EVENT_STATE,
  RESET_USER_SPECIAL_EVENT,
  UPDATE_USER_SPECIAL_EVENT_POINTS,
} from '../../../graphql';

type Props = {
  userId: string;
  specialEventId: string | undefined;
  scoreDetails?: NonNullable<NonNullable<SearchUserQuery['user']>['currentSpecialEvent']>['meScoreDetails'];
};

type Milestone = { id: string; score: number; isClaimed: boolean };
type SequentialMission = {
  id: string;
  progress: number;
  progressGoal: number;
  completedDate?: string | null;
  claimedDate?: string | null;
};

/** What the BE should clear. Everything omitted defaults to true on the BE, so always be explicit. */
type ResetScope = {
  milestoneIds?: string[];
  resetClaimedMilestones: boolean;
  resetRankGroupReward: boolean;
  resetMissions: boolean;
};

const ClaimedIcon = ({ claimed }: { claimed: boolean }) => (
  <i
    className={claimed ? 'fas fa-check-square' : 'fas fa-times-circle'}
    style={{ color: claimed ? '#2bf702' : '#FF0000' }}
  />
);

const MilestoneTable = ({
  title,
  milestones,
  disabled,
  onUnclaim,
}: {
  title: string;
  milestones: Milestone[];
  disabled: boolean;
  onUnclaim: (milestone: Milestone) => void;
}) => (
  <Card style={{ marginBottom: 8 }}>
    <Card.Header className="text-center py-1">
      <Card.Title className="mb-0 small text-uppercase">{title}</Card.Title>
    </Card.Header>
    <Card.Body className="p-0">
      <Table bordered responsive hover size="sm" className="mb-0">
        <thead className="text-center">
          <tr>
            <th>Points</th>
            <th>Claimed</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {milestones.map((milestone) => (
            <tr key={milestone.id}>
              <td className="text-center">{milestone.score}</td>
              <td className="text-center">
                <ClaimedIcon claimed={milestone.isClaimed} />
              </td>
              <td className="text-center">
                <Button
                  size="sm"
                  variant="outline-warning"
                  disabled={disabled || !milestone.isClaimed}
                  onClick={() => onUnclaim(milestone)}
                >
                  Un-claim
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card.Body>
  </Card>
);

/** The four point sources, in the order they are shown. Drives both the form and the save payload. */
const POINT_FIELDS: { type: SpecialEventScoreDetailType; label: string }[] = [
  { type: SpecialEventScoreDetailType.CONTENT_PACK_LEVEL_UP, label: 'Content pack' },
  { type: SpecialEventScoreDetailType.LIVE_SHOW, label: 'Live show' },
  { type: SpecialEventScoreDetailType.MISSION, label: 'Mission' },
  { type: SpecialEventScoreDetailType.TOURNAMENT, label: 'Tournament' },
];

/** Kept as strings so the field can be empty mid-typing; converted on save. */
type PointsForm = Record<SpecialEventScoreDetailType, string>;
const EMPTY_POINTS: PointsForm = {
  [SpecialEventScoreDetailType.CONTENT_PACK_LEVEL_UP]: '0',
  [SpecialEventScoreDetailType.LIVE_SHOW]: '0',
  [SpecialEventScoreDetailType.MISSION]: '0',
  [SpecialEventScoreDetailType.TOURNAMENT]: '0',
};

const FestivalProgressDisplay = ({ userId, specialEventId, scoreDetails }: Props) => {
  const [expanded, setExpanded] = useState(false);
  const [points, setPoints] = useState<PointsForm>(EMPTY_POINTS);
  const [resetError, setResetError] = useState<string | null>(null);

  const [updateUserSpecialEventPoints, { loading: updatePointsLoading }] = useMutation(
    UPDATE_USER_SPECIAL_EVENT_POINTS
  );

  const [isRefetching, setIsRefetching] = useState(false);
  const {
    data: stateData,
    loading: stateLoading,
    refetch,
  } = useQuery(GET_USER_SPECIAL_EVENT_STATE, {
    variables: { property: userId, type: UserSearchType.BY_ID },
    // Only once the card is open: resolving the milestone ladders is not cheap, and this
    // component renders on every user lookup, most of which never touch the Festival section.
    skip: !userId || _.isNil(specialEventId) || !expanded,
    fetchPolicy: 'network-only',
  });

  const [resetUserSpecialEvent, { loading: resetLoading }] = useMutation(RESET_USER_SPECIAL_EVENT);

  const currentSpecialEvent = stateData?.user?.currentSpecialEvent;
  const milestones: Milestone[] = currentSpecialEvent?.milestones ?? [];
  const plusMilestones: Milestone[] = currentSpecialEvent?.plusMilestones ?? [];
  const sequentialMissions: SequentialMission[] = currentSpecialEvent?.userMissions?.sequentialMissions ?? [];
  const hasClaimedMilestone = [...milestones, ...plusMilestones].some(({ isClaimed }) => isClaimed);

  // refetch() always goes to the network. useLazyQuery's execute function does not guarantee
  // that on a repeat call with unchanged variables, which left the tables showing stale claim
  // state right after an un-claim.
  const refreshState = useCallback(() => {
    setIsRefetching(true);
    return refetch().finally(() => setIsRefetching(false));
  }, [refetch]);

  useEffect(() => {
    if (_.isNil(scoreDetails)) {
      return;
    }
    setPoints((current) => {
      const next = { ...current };
      scoreDetails.forEach(({ score, type }) => {
        next[type] = String(score);
      });
      return next;
    });
  }, [scoreDetails]);

  const handleChange = (type: SpecialEventScoreDetailType, value: string) => {
    setPoints((current) => ({ ...current, [type]: value }));
  };

  const handleSave = () => {
    if (_.isNil(specialEventId)) {
      return;
    }
    const userScoreDetails = POINT_FIELDS.map(({ type }) => ({ type, score: Number(points[type]) || 0 }));
    updateUserSpecialEventPoints({ variables: { input: { specialEventId, userId, userScoreDetails } } });
  };

  const handleReset = useCallback(
    async (label: string, scope: ResetScope) => {
      if (_.isNil(specialEventId)) {
        return;
      }
      if (
        !window.confirm(
          `${label} on the current Festival for this user? Items and currency already granted are NOT taken back.`
        )
      ) {
        return;
      }
      setResetError(null);
      try {
        await resetUserSpecialEvent({ variables: { input: { specialEventId, userId, ...scope } } });
        await refreshState();
      } catch (error) {
        setResetError((error as Error).message);
      }
    },
    [resetUserSpecialEvent, refreshState, specialEventId, userId]
  );

  const unclaimMilestone = useCallback(
    (milestone: Milestone) =>
      handleReset(`Un-claim the ${milestone.score} pts milestone`, {
        milestoneIds: [milestone.id],
        resetClaimedMilestones: true,
        resetRankGroupReward: false,
        resetMissions: false,
      }),
    [handleReset]
  );

  const busy = updatePointsLoading || resetLoading || stateLoading || isRefetching;

  return (
    <Card className="mb-2 shadow-sm border-0">
      <Card.Body className="py-2">
        <Accordion>
          <Accordion.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded(!expanded)}>
            <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
              Current Festival points
              <Button variant="link">
                {!expanded && <i className="fas fa-chevron-down" />}
                {expanded && <i className="fas fa-chevron-up" />}
              </Button>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <>
              {!_.isNil(specialEventId) ? (
                <Form>
                  <Form.Row className="align-items-end">
                    {POINT_FIELDS.map(({ type, label }) => (
                      <Form.Group as={Col} xs={6} md={2} key={type} className="mb-2">
                        <Form.Label className="small text-muted mb-1">{label}</Form.Label>
                        <Form.Control
                          size="sm"
                          type="number"
                          value={points[type]}
                          onChange={(event) => handleChange(type, event.target.value)}
                        />
                      </Form.Group>
                    ))}
                    <Col xs="auto" className="mb-2">
                      <Button size="sm" disabled={busy} variant="primary" onClick={handleSave}>
                        Save points
                      </Button>
                    </Col>
                  </Form.Row>
                </Form>
              ) : (
                <div>No active Festival</div>
              )}
              {!_.isNil(specialEventId) && (
                <>
                  <hr className="my-2" />
                  <div className="small text-muted text-uppercase mb-2">Reset test account</div>
                  {resetError && (
                    <Alert variant="danger" className="py-2">
                      {resetError}
                    </Alert>
                  )}
                  <div className="d-flex justify-content-start align-items-baseline flex-wrap mb-3" style={{ gap: 8 }}>
                    <Button size="sm" disabled={busy} onClick={refreshState}>
                      {stateLoading || isRefetching ? <Spinner animation="border" size="sm" /> : 'Refresh'}
                    </Button>
                    <Button
                      size="sm"
                      variant="warning"
                      disabled={busy || !hasClaimedMilestone}
                      onClick={() =>
                        handleReset('Un-claim every milestone', {
                          resetClaimedMilestones: true,
                          resetRankGroupReward: false,
                          resetMissions: false,
                        })
                      }
                    >
                      <i className="fas fa-box-open" /> Un-claim all milestones
                    </Button>
                    <Button
                      size="sm"
                      variant="warning"
                      disabled={busy}
                      onClick={() =>
                        handleReset('Un-claim the rank group reward and drop the frozen rank', {
                          resetClaimedMilestones: false,
                          resetRankGroupReward: true,
                          resetMissions: false,
                        })
                      }
                    >
                      <i className="fas fa-trophy" /> Reset rank group reward
                    </Button>
                    <Button
                      size="sm"
                      variant="warning"
                      disabled={busy}
                      onClick={() =>
                        handleReset('Wipe the Festival mission progress', {
                          resetClaimedMilestones: false,
                          resetRankGroupReward: false,
                          resetMissions: true,
                        })
                      }
                    >
                      <i className="fas fa-tasks" /> Reset missions
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={busy}
                      onClick={() =>
                        handleReset('Un-claim every reward AND wipe the mission progress', {
                          resetClaimedMilestones: true,
                          resetRankGroupReward: true,
                          resetMissions: true,
                        })
                      }
                    >
                      <i className="fas fa-battery-empty" /> Full reset
                    </Button>
                  </div>

                  {currentSpecialEvent && (
                    <>
                      <div className="mb-2">
                        <strong>{currentSpecialEvent.name}</strong> — total {currentSpecialEvent.meScore} pts — rank
                        group reward <ClaimedIcon claimed={Boolean(currentSpecialEvent.isRankGroupRewardClaimed)} />
                      </div>
                      <Form.Text muted className="mb-2">
                        A basic milestone and the plus milestone of the same score share one claim entry, so
                        un-claiming one row clears that score on both tracks.
                      </Form.Text>
                      <div className="d-flex flex-wrap" style={{ gap: 12 }}>
                        {milestones.length > 0 && (
                          <MilestoneTable
                            title="Milestones"
                            milestones={milestones}
                            disabled={busy}
                            onUnclaim={unclaimMilestone}
                          />
                        )}
                        {plusMilestones.length > 0 && (
                          <MilestoneTable
                            title="Plus milestones"
                            milestones={plusMilestones}
                            disabled={busy}
                            onUnclaim={unclaimMilestone}
                          />
                        )}
                        {sequentialMissions.length > 0 && (
                          <Card style={{ marginBottom: 8 }}>
                            <Card.Header className="text-center py-1">
                              <Card.Title className="mb-0 small text-uppercase">Missions</Card.Title>
                            </Card.Header>
                            <Card.Body className="p-0">
                              <Table bordered responsive hover size="sm" className="mb-0">
                                <thead className="text-center">
                                  <tr>
                                    <th>#</th>
                                    <th>Progress</th>
                                    <th>Completed</th>
                                    <th>Claimed</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sequentialMissions.map((mission, index) => (
                                    <tr key={mission.id}>
                                      <td className="text-center">{index + 1}</td>
                                      <td className="text-center">
                                        {mission.progress} / {mission.progressGoal}
                                      </td>
                                      <td className="text-center">
                                        <ClaimedIcon claimed={!_.isNil(mission.completedDate)} />
                                      </td>
                                      <td className="text-center">
                                        <ClaimedIcon claimed={!_.isNil(mission.claimedDate)} />
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </Card.Body>
                          </Card>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>
    </Card>
  );
};

export default FestivalProgressDisplay;
